import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {Object} VariantPriceConfig
 * @property {number} [automatic_discount_percent]
 * @property {string} [discount_label_template]
 * @property {string} [discount_label_placeholder]
 * @property {string} [price_regular_label]
 * @property {string} [price_sale_label]
 */

/**
 * @typedef {Object} VariantPriceData
 * @property {number} price
 * @property {number} [sale_price]
 * @property {number} compare_at_price
 * @property {number} [regular_price]
 * @property {string} price_money
 * @property {string} [regular_money]
 * @property {string} compare_at_money
 * @property {number} discount_percent
 * @property {string} discount_label
 * @property {boolean} [show_sale]
 */

/**
 * Formats cents using the shop money format (Shopify-compatible).
 * @param {number} cents
 * @param {string | null | undefined} moneyFormat
 */
function formatMoneyFromCents(cents, moneyFormat) {
  const shopify = /** @type {{ formatMoney?: (cents: number, format?: string) => string; money_format?: string }} */ (
    window.Shopify
  );
  const formatString = moneyFormat || shopify?.money_format;

  if (shopify?.formatMoney) {
    return formatString ? shopify.formatMoney(cents, formatString) : shopify.formatMoney(cents);
  }

  if (!formatString) {
    return (cents / 100).toFixed(2);
  }

  /**
   * @param {number} number
   * @param {number} [precision]
   * @param {string} [thousands]
   * @param {string} [decimal]
   */
  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (Number.isNaN(number) || number == null) return '0';

    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = (parts[0] ?? '0').replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const centsPart = parts[1] ? decimal + parts[1] : '';

    return dollars + centsPart;
  };

  const placeholderMatch = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
  const placeholder = placeholderMatch?.[1] ?? 'amount_with_comma_separator';

  /** @type {Record<string, string>} */
  const formattedValues = {
    amount: formatWithDelimiters(cents, 2, ',', '.'),
    amount_no_decimals: formatWithDelimiters(cents, 0, ',', '.'),
    amount_with_comma_separator: formatWithDelimiters(cents, 2, '.', ','),
    amount_no_decimals_with_comma_separator: formatWithDelimiters(cents, 0, '.', ','),
    amount_with_apostrophe_separator: formatWithDelimiters(cents, 2, "'", '.'),
  };

  const value = formattedValues[placeholder] ?? formattedValues.amount_with_comma_separator ?? '';

  return formatString.replace(/\{\{\s*\w+\s*\}\}/, value);
}

/**
 * @param {string | undefined} moneyString
 */
function isValidFormattedMoney(moneyString) {
  return Boolean(moneyString && !moneyString.includes('{{'));
}

/**
 * @param {number} percent
 * @param {VariantPriceConfig} config
 */
function buildDiscountLabel(percent, config) {
  if (config.discount_label_template && config.discount_label_placeholder) {
    return config.discount_label_template.split(config.discount_label_placeholder).join(String(percent));
  }

  return `-${percent}%`;
}

/**
 * Resolves sale display from variant JSON, compare-at, or automatic-discount config.
 * @param {VariantPriceData} data
 * @param {VariantPriceConfig} config
 * @param {string | undefined} moneyFormat
 * @returns {{ regularMoney: string, saleMoney: string, discountLabel: string } | null}
 */
function resolveAddToCartSalePrices(data, config, moneyFormat) {
  /**
   * @param {number} cents
   * @param {string | undefined} formatted
   */
  const money = (cents, formatted) => {
    if (isValidFormattedMoney(formatted)) return formatted;
    return formatMoneyFromCents(cents, moneyFormat);
  };

  if (data.compare_at_price > data.price) {
    const percent =
      data.discount_percent || Math.round(((data.compare_at_price - data.price) * 100) / data.compare_at_price);
    const regularMoney = money(data.compare_at_price, data.compare_at_money);
    const saleMoney = money(data.price, data.price_money);

    if (!isValidFormattedMoney(regularMoney) || !isValidFormattedMoney(saleMoney)) return null;

    return {
      regularMoney,
      saleMoney,
      discountLabel: data.discount_label || buildDiscountLabel(percent, config),
    };
  }

  if (data.show_sale) {
    const regularCents = data.regular_price || 0;
    const saleCents = data.sale_price ?? data.price;

    if (regularCents > saleCents) {
      const percent = data.discount_percent || Math.round(((regularCents - saleCents) * 100) / regularCents);
      const regularMoney = money(regularCents, data.regular_money);
      const saleMoney = money(saleCents, data.price_money);

      if (!isValidFormattedMoney(regularMoney) || !isValidFormattedMoney(saleMoney)) return null;

      return {
        regularMoney,
        saleMoney,
        discountLabel: data.discount_label || buildDiscountLabel(percent, config),
      };
    }
  }

  const autoPercent = Number(config.automatic_discount_percent) || 0;
  if (autoPercent > 0 && data.price > 0) {
    const saleCents = Math.round((data.price * (100 - autoPercent)) / 100);

    if (saleCents < data.price) {
      const regularMoney = money(data.price, data.regular_money);
      const saleMoney = money(saleCents, undefined);

      if (!isValidFormattedMoney(regularMoney) || !isValidFormattedMoney(saleMoney)) return null;

      return {
        regularMoney,
        saleMoney,
        discountLabel: buildDiscountLabel(autoPercent, config),
      };
    }
  }

  return null;
}

/**
 * @param {HTMLElement} priceEl
 * @param {{ regularMoney: string, saleMoney: string, discountLabel: string, priceRegularLabel: string, priceSaleLabel: string }} prices
 */
function renderAddToCartSalePrice(priceEl, prices) {
  priceEl.classList.add('add-to-cart-price--on-sale');
  priceEl.innerHTML = `
    <span class="add-to-cart-price__compare" role="group">
      <span class="visually-hidden">${prices.priceRegularLabel}&nbsp;</span>
      <s class="compare-at-price add-to-cart-price__compare-value">${prices.regularMoney}</s>
    </span>
    <span class="add-to-cart-price__sale" role="group">
      <span class="visually-hidden">${prices.priceSaleLabel}&nbsp;</span>
      <span class="price add-to-cart-price__current">${prices.saleMoney}</span>
      <span class="add-to-cart-price__badge">${prices.discountLabel}</span>
    </span>
  `;
}

/**
 * @param {HTMLElement | null | undefined} container
 * @param {string | number | null | undefined} variantId
 */
function syncAddToCartPriceDisplay(container, variantId) {
  if (!container || variantId == null || variantId === '') return;

  const priceDataScript = container.querySelector('[data-variant-prices]');
  const priceEl = /** @type {HTMLElement | null} */ (container.querySelector('[ref="addToCartPrice"]'));
  if (!priceDataScript?.textContent || !priceEl) return;

  try {
    /** @type {Record<string, VariantPriceData | VariantPriceConfig> & { __config__?: VariantPriceConfig }} */
    const pricesByVariant = JSON.parse(priceDataScript.textContent);
    const config = pricesByVariant.__config__ ?? {};
    const data = /** @type {VariantPriceData | undefined} */ (pricesByVariant[String(variantId)]);
    if (!data) return;

    const labels = {
      priceRegularLabel: config.price_regular_label ?? '',
      priceSaleLabel: config.price_sale_label ?? '',
    };

    const moneyFormat = container.getAttribute('data-money-format') ?? undefined;
    const salePrices = resolveAddToCartSalePrices(data, config, moneyFormat);

    if (salePrices) {
      renderAddToCartSalePrice(priceEl, { ...salePrices, ...labels });
      return;
    }

    const listMoney = isValidFormattedMoney(data.price_money)
      ? data.price_money
      : formatMoneyFromCents(data.price, moneyFormat);

    priceEl.classList.remove('add-to-cart-price--on-sale');
    priceEl.innerHTML = `<span class="price add-to-cart-price__current">${listMoney}</span>`;
  } catch {
    // Keep server-rendered price if JSON is invalid
  }
}

/**
 * Applies line-item prices from cart.js (automatic discounts) when the variant is already in the cart.
 * @param {HTMLElement | null | undefined} container
 * @param {string | number | null | undefined} variantId
 */
async function syncAddToCartPriceFromCart(container, variantId) {
  if (!container || variantId == null || variantId === '') return;

  const priceEl = /** @type {HTMLElement | null} */ (container.querySelector('[ref="addToCartPrice"]'));
  const priceDataScript = container.querySelector('[data-variant-prices]');
  if (!priceEl || !priceDataScript?.textContent) return;

  /** @type {Record<string, VariantPriceData | VariantPriceConfig> & { __config__?: VariantPriceConfig }} */
  let pricesByVariant;
  try {
    pricesByVariant = JSON.parse(priceDataScript.textContent);
  } catch {
    return;
  }

  const config = pricesByVariant.__config__ ?? {};
  const moneyFormat = container.getAttribute('data-money-format') ?? undefined;

  try {
    const response = await fetch('/cart.js');
    if (!response.ok) return;

    const cart = await response.json();
    const item = cart.items?.find((/** @type {{ variant_id: number }} */ line) => String(line.variant_id) === String(variantId));
    const variantData = /** @type {VariantPriceData | undefined} */ (pricesByVariant[String(variantId)]);

    if (!item) return;

    const original = item.original_price;
    const final = item.final_price ?? item.price;
    if (original <= final) return;

    if (
      variantData?.show_sale &&
      isValidFormattedMoney(variantData.regular_money) &&
      isValidFormattedMoney(variantData.price_money) &&
      variantData.sale_price === final &&
      variantData.regular_price === original
    ) {
      return;
    }

    const percent = Math.round(((original - final) * 100) / original);

    let regularMoney = formatMoneyFromCents(original, moneyFormat);
    let saleMoney = formatMoneyFromCents(final, moneyFormat);

    if (
      variantData?.regular_money &&
      isValidFormattedMoney(variantData.regular_money) &&
      variantData.regular_price === original
    ) {
      regularMoney = variantData.regular_money;
    }

    if (
      variantData?.price_money &&
      isValidFormattedMoney(variantData.price_money) &&
      variantData.sale_price === final
    ) {
      saleMoney = variantData.price_money;
    }

    if (!isValidFormattedMoney(regularMoney) || !isValidFormattedMoney(saleMoney)) return;

    renderAddToCartSalePrice(priceEl, {
      regularMoney,
      saleMoney,
      discountLabel: buildDiscountLabel(percent, config),
      priceRegularLabel: config.price_regular_label ?? '',
      priceSaleLabel: config.price_sale_label ?? '',
    });
  } catch {
    // Keep JSON-based display if cart fetch fails
  }
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    void this.#syncAddToCartPrice();
  }

  /**
   * Syncs add-to-cart price/discount display (variant JSON, then cart line if discounted).
   * @param {string | number | null | undefined} [variantId]
   */
  async #syncAddToCartPrice(variantId) {
    const id = variantId ?? this.refs.variantId?.value;
    const container = this.refs.addToCartButtonContainer;
    syncAddToCartPriceDisplay(container, id);
    await syncAddToCartPriceFromCart(container, id);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    const isOwnSource =
      event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component';

    if (!isOwnSource) {
      const cart = /** @type {Cart} */ (event.detail?.resource);
      if (cart?.items) {
        this.#updateCartQuantityFromData(cart);
      } else {
        await this.#fetchAndUpdateCartQuantity();
      }
    }

    void this.#syncAddToCartPrice();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();
          await this.#syncAddToCartPrice(String(id));

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    void this.#syncAddToCartPrice(event.detail.resource?.id);

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
